/**
 * US Eastern civil-day helpers for admin dashboards.
 * Kept in sync with functions/src/reporting/timezoneEt.ts (getDashboardPageMetrics).
 */
export type Ymd = { year: number; month: number; day: number };

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function ymdToDayKey(ymd: Ymd): string {
  return `${ymd.year}-${pad2(ymd.month)}-${pad2(ymd.day)}`;
}

export function addDaysCivil(ymd: Ymd, deltaDays: number): Ymd {
  const d = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  };
}

export function getNowYmdInTimeZone(timeZone: string, now: Date = new Date()): Ymd {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = dtf.formatToParts(now);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
  };
}

export function getDayKeyTodayInTimeZone(timeZone: string, now: Date = new Date()): string {
  return ymdToDayKey(getNowYmdInTimeZone(timeZone, now));
}

export function getDayKeyYesterdayInTimeZone(timeZone: string, now: Date = new Date()): string {
  const today = getNowYmdInTimeZone(timeZone, now);
  return ymdToDayKey(addDaysCivil(today, -1));
}

function getTimeZoneOffsetMs(dateUtc: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(dateUtc);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;

  const asUtcMs = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return asUtcMs - dateUtc.getTime();
}

export function zonedTimeToUtc(params: {
  timeZone: string;
  ymd: Ymd;
  hour?: number;
  minute?: number;
  second?: number;
  millisecond?: number;
}): Date {
  const {
    timeZone,
    ymd,
    hour = 0,
    minute = 0,
    second = 0,
    millisecond = 0,
  } = params;

  const localAsUtcMs = Date.UTC(ymd.year, ymd.month - 1, ymd.day, hour, minute, second, millisecond);
  const offset1 = getTimeZoneOffsetMs(new Date(localAsUtcMs), timeZone);
  let utcMs = localAsUtcMs - offset1;

  const offset2 = getTimeZoneOffsetMs(new Date(utcMs), timeZone);
  if (offset2 !== offset1) {
    utcMs = localAsUtcMs - offset2;
  }

  return new Date(utcMs);
}

export function startOfDayUtc(dayKey: string, timeZone: string): Date {
  const [y, m, d] = dayKey.split("-").map((v) => Number(v));
  return zonedTimeToUtc({ timeZone, ymd: { year: y, month: m, day: d }, hour: 0, minute: 0, second: 0, millisecond: 0 });
}

export function nextDayStartUtc(dayKey: string, timeZone: string): Date {
  const [y, m, d] = dayKey.split("-").map((v) => Number(v));
  const next = addDaysCivil({ year: y, month: m, day: d }, 1);
  return zonedTimeToUtc({ timeZone, ymd: next, hour: 0, minute: 0, second: 0, millisecond: 0 });
}

/** Admin metrics default: same as Cloud Function getDashboardPageMetrics */
export const DASHBOARD_METRICS_TIMEZONE = "America/New_York";
